import { styled, alpha } from "@material-ui/core/styles";

// ----------------------------------------------------------------------

const CalendarStyle = styled("div")(({ theme }) => ({
  width: "calc(100% + 2px)",
  marginLeft: -1,
  marginBottom: -1,
  "& .fc": {
    "--fc-list-event-dot-width": "8px",
    "--fc-border-color": theme.palette.divider,
    "--fc-event-border-color": theme.palette.info.light,
    "--fc-now-indicator-color": theme.palette.error.main,
    "--fc-today-bg-color": theme.palette.action.selected,
    "--fc-page-bg-color": theme.palette.background.default,
    "--fc-neutral-bg-color": theme.palette.background.neutral,
    "--fc-list-event-hover-bg-color": theme.palette.action.hover,
    "--fc-highlight-color": alpha(theme.palette.primary.main, 0.08)
  },

  "& .fc .fc-license-message": { display: "none" },
  "& .fc a": { color: theme.palette.text.primary },

  // Table Head
  "& .fc .fc-col-header ": {
    boxShadow: `inset 0 -1px 0 ${theme.palette.divider}`,
    "& th": { borderColor: "transparent" },
    "& .fc-col-header-cell-cushion": {
      ...theme.typography.subtitle2,
      padding: "13px 0"
    }
  },

  // Event — solid, rich colour block with white text (the colour itself is
  // deepened per-event in CalendarPage via toRichEventColor, so white always
  // reads). `currentColor` is the event's textColor set by FullCalendar.
  "& .fc-event": {
    backgroundColor: "transparent !important"
  },
  // No border on the event itself. FullCalendar draws a 1px event border; with
  // the event background transparent, that 1px ring shows the grid behind it as
  // a stray outline around the solid `.fc-event-main` card. Drop it so the card
  // fills edge to edge.
  "& .fc .fc-event": {
    border: 0,
    backgroundColor: "transparent",
    boxShadow: "none"
  },
  "& .fc .fc-event .fc-event-main": {
    padding: "3px 8px",
    borderRadius: 6,
    backgroundColor: "currentColor",
    // Subtle single-hue sheen: a soft highlight up top fading to a touch of
    // shade at the bottom, over the solid colour — gives the card depth/punch
    // without introducing a second colour.
    backgroundImage:
      "linear-gradient(160deg, rgba(255, 255, 255, 0.22) 0%, rgba(255, 255, 255, 0) 45%, rgba(0, 0, 0, 0.12) 100%)",
    boxShadow: "0 1px 2px 0 rgba(33, 43, 54, 0.24)",
    transition: theme.transitions.create("filter"),
    "&:hover": {
      filter: "brightness(1.1)"
    }
  },
  // White text + checkbox, scoped under `.fc-event-main` — which only exists on
  // block events (week/day/month). The agenda/list view has no `.fc-event-main`,
  // so its rows keep the default dark-on-paper text.
  "& .fc .fc-event .fc-event-main .fc-event-main-frame": {
    fontSize: 13,
    lineHeight: "20px",
    color: theme.palette.common.white
  },
  "& .fc .fc-event .fc-event-main .MuiCheckbox-root": {
    color: theme.palette.common.white
  },
  // List/agenda view keeps its own typography.
  "& .fc .fc-list-event .fc-event-main-frame": {
    fontSize: 13,
    lineHeight: "20px"
  },
  "& .fc .fc-daygrid-event .fc-event-title": {
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis"
  },
  "& .fc .fc-event .fc-event-time": {
    padding: 0,
    overflow: "unset",
    fontWeight: theme.typography.fontWeightBold
  },

  // Popover
  "& .fc .fc-popover": {
    border: 0,
    overflow: "hidden",
    boxShadow: theme.customShadows.z20,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper
  },
  "& .fc .fc-popover-header": {
    ...theme.typography.subtitle2,
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[500_12],
    borderBottom: `solid 1px ${theme.palette.divider}`
  },
  "& .fc .fc-popover-close": {
    opacity: 0.48,
    transition: theme.transitions.create("opacity"),
    "&:hover": { opacity: 1 }
  },
  "& .fc .fc-more-popover .fc-popover-body": {
    padding: theme.spacing(1.5)
  },
  "& .fc .fc-popover-body": {
    "& .fc-daygrid-event.fc-event-start, & .fc-daygrid-event.fc-event-end": {
      margin: "2px 0"
    }
  },

  // Month View
  "& .fc .fc-day-other .fc-daygrid-day-top": {
    opacity: 1,
    "& .fc-daygrid-day-number": {
      color: theme.palette.text.disabled
    }
  },
  "& .fc .fc-daygrid-day-number": {
    ...theme.typography.body2,
    padding: theme.spacing(1, 1, 0)
  },
  "& .fc .fc-daygrid-event": {
    marginTop: 4
  },
  "& .fc .fc-daygrid-event.fc-event-start, & .fc .fc-daygrid-event.fc-event-end": {
    marginLeft: 4,
    marginRight: 4
  },
  "& .fc .fc-daygrid-more-link": {
    ...theme.typography.caption,
    color: theme.palette.text.secondary
  },

  // Week & Day View
  "& .fc .fc-timegrid-axis-cushion": {
    ...theme.typography.body2,
    color: theme.palette.text.secondary
  },
  "& .fc .fc-timegrid-slot-label-cushion": {
    ...theme.typography.body2
  },

  // Agenda View
  "& .fc-direction-ltr .fc-list-day-text, .fc-direction-rtl .fc-list-day-side-text, .fc-direction-ltr .fc-list-day-side-text, .fc-direction-rtl .fc-list-day-text":
    {
      ...theme.typography.subtitle2
    },
  "& .fc .fc-list-event": {
    ...theme.typography.body2,
    "& .fc-list-event-time": {
      color: theme.palette.text.secondary
    }
  },
  "& .fc .fc-list-table": {
    "& th, td": {
      borderColor: "transparent"
    }
  }
}));

export default CalendarStyle;
